import { gql, useMutation } from '@apollo/client';
import { capitalize } from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button } from 'reactstrap';
import styled from 'styled-components';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { TEAM_ROLES } from '../../constants/teamRoles';
import { UpsertTeamMembersMutation } from '../../mutations';
import { MyTeamsQuery } from '../../queries';
import { asQuantityWithUnit } from '../../utils/formatters';
import { useRemainingPaidSeats, useUserRole } from '../../utils/hooks';
import { getTeamBillingPath } from '../../utils/team';
import Endash from '../basics/Endash';
import LineItemsTable from '../tables/LineItemsTable';
import { ShrinkContentColumn } from '../ShrinkContent';
import { DropdownButtonField } from '../templates/template-components';
import useMutateWithState from '../../hooks/useMutateWithState';

const UPGRADE_MESSAGE =
  'Number of editors exceeds number of paid seats. Please downgrade some users to viewer or upgrade your subscription.';

const TableTitle = styled.h1`
  margin: var(--header-margin-y) var(--cell-padding) 0;
`;

const ScrollingForm = styled(Form)`
  overflow-y: scroll;
`;

const TableCellButton = styled(Button)`
  padding: 0;
  border: none;
  vertical-align: baseline;
  line-height: inherit;
`;

const TableDropdownButton = styled(DropdownButtonField)`
  border: none;
  padding: 0;
  vertical-align: baseline;
  line-height: inherit;
  width: 100%;
`;

export const ReinviteTeamMemberMutation = gql`
  mutation ReinviteTeamMember($input: ReinviteTeamMemberInput!) {
    reinviteTeamMember(input: $input) {
      errors
      result {
        id
      }
    }
  }
`;

export const RemoveTeamMemberMutation = gql`
  mutation RemoveTeamMember($input: RemoveTeamMemberInput!) {
    removeTeamMember(input: $input) {
      errors
      result {
        id
      }
    }
  }
`;

const ResendButton = ({
  team,
  member,
  loading,
  refetchQueries,
  mutateAndSetState,
}) => {
  const [reinviteMember] = useMutation(ReinviteTeamMemberMutation, {
    refetchQueries,
  });

  const { isPending, id: memberId } = member;
  const { id, updatedAt } = team;

  const onClick = () => {
    mutateAndSetState(
      () =>
        reinviteMember({ variables: { input: { id, memberId, updatedAt } } }),
      'Team member invitation resent!',
    );
  };

  return (
    !!isPending && (
      <TableCellButton
        disabled={loading}
        color="link"
        size="sm"
        onClick={onClick}
      >
        Resend Invite
      </TableCellButton>
    )
  );
};

const RemoveButton = ({
  team,
  member,
  loading,
  refetchQueries,
  mutateAndSetState,
}) => {
  const [removeMember] = useMutation(RemoveTeamMemberMutation, {
    refetchQueries: [...refetchQueries, { query: MyTeamsQuery }],
  });

  const { isPending, id: memberId } = member;
  const { id, updatedAt } = team;

  const onClick = () => {
    mutateAndSetState(
      () => removeMember({ variables: { input: { id, memberId, updatedAt } } }),
      'Team member removed!',
    );
  };

  return (
    <TableCellButton
      disabled={loading}
      color="link"
      size="sm"
      onClick={onClick}
    >
      {isPending ? 'Cancel Invite' : 'Remove User'}
    </TableCellButton>
  );
};

const RoleDropdownButton = ({ memberIdx, loading }) => {
  return (
    <TableDropdownButton
      name={`members[${memberIdx}].role`}
      disabled={loading}
      color="link"
      options={TEAM_ROLES}
    />
  );
};

const SHARED_COLS = [
  {
    accessor: 'firstName',
    displayString: 'First Name',
  },
  {
    accessor: 'lastName',
    displayString: 'Last Name',
  },
  {
    accessor: 'email',
    displayString: 'Email',
  },
];

const READONLY_COLS = [
  ...SHARED_COLS,
  {
    accessor: 'role',
    displayString: 'Permissions',
    format: (role) => capitalize(role),
  },
  {
    accessor: 'status',
    displayString: 'Status',
  },
];

const COLS = [
  ...SHARED_COLS,
  {
    accessor: 'roleProps',
    displayString: 'Permissions',
    format: (props) => <RoleDropdownButton {...props} />,
  },
  {
    accessor: 'status',
    displayString: 'Status',
  },
  {
    accessor: 'resend',
    displayString: '',
    format: (props) => <ResendButton {...props} />,
    style: { minWidth: '100px' },
  },
  {
    accessor: 'remove',
    displayString: '',
    format: (props) => <RemoveButton {...props} />,
  },
];

const TeamMembersTable = ({
  msg,
  data,
  refetchQueries,
  readonly,
  showSettingsLink,
}) => {
  const navigate = useNavigate();
  const { isSuperadmin } = useUserRole();

  const [{ loading }, mutateAndSetState] = useMutateWithState();
  const { hasValidSubscription } = data?.team || {};
  const { numPaidSeats } = useRemainingPaidSeats(data?.team?.id, readonly);
  const numPaidSeatsInUse =
    data?.team?.members?.filter(
      ({ role }) => role === 'editor' || role === 'admin',
    ).length || 0;
  const showSeatCount =
    !readonly && // User should be able to query for number of paid seats
    (hasValidSubscription || numPaidSeats - numPaidSeatsInUse < 0);

  const [upsertTeamMembers] = useMutation(UpsertTeamMembersMutation, {
    refetchQueries: [...refetchQueries, { query: MyTeamsQuery }],
  });

  const getItems = (itemData) => {
    const {
      team,
      team: { members },
    } = itemData;
    return members.map((member, memberIdx) => {
      const props = {
        team,
        member,
        memberIdx,
        loading,
        refetchQueries,
        mutateAndSetState,
      };
      return {
        ...member,
        status: member.isPending ? 'Not Signed In' : 'Active',
        resend: props,
        remove: props,
        roleProps: props,
      };
    });
  };

  const buttonProps = readonly
    ? {}
    : {
        createButton: 'Add Member',
        createButtonOnClick: () => navigate('new'),
        saveButton: true,
      };

  const testMemberRoles = (members) => {
    if (isSuperadmin) return true; // Superadmins can always edit roles
    const updatedPaidSeatsInUse = (members || []).filter(
      (m) => m.role === 'admin' || m.role === 'editor',
    ).length;

    return hasValidSubscription
      ? // If subscription is valid, only allow up to number of paid seats
        updatedPaidSeatsInUse <= numPaidSeats
      : // If subscription invalid, only allow less than or equal to current seats in use
        updatedPaidSeatsInUse <= numPaidSeatsInUse;
  };

  const validationSchema = yup.object().shape({
    members: yup
      .array()
      .nullable()
      .of(
        yup.object().shape({
          id: yup.string().required('Member id required'),
          role: yup.string().required('Role required'),
        }),
      )
      .test({
        test: (members) => members?.some((m) => m.role === 'admin'),
        message: 'Team must have at least one admin',
      })
      .test({
        test: testMemberRoles,
        message: UPGRADE_MESSAGE,
      }),
  });

  const initialValues = {
    members: (data?.team?.members || []).map(({ id, role }) => ({ id, role })),
  };

  return (
    <ShrinkContentColumn>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const castValues = validationSchema.cast(values);
          const transformedValues = {
            ...castValues,
            id: data?.team?.id,
            updatedAt: data?.team?.updatedAt,
          };
          return mutateAndSetState(
            () =>
              upsertTeamMembers({
                variables: {
                  input: transformedValues,
                },
              }),
            'Team member permissions saved',
          );
        }}
      >
        {({ isValid, errors }) => (
          <>
            <TableTitle>Team Members</TableTitle>
            {!isValid && (
              <Alert className="my-1" color="danger">
                {errors.members}
              </Alert>
            )}
            <ScrollingForm>
              <LineItemsTable
                data={data}
                msg={msg}
                cols={readonly ? READONLY_COLS : COLS}
                getItems={getItems}
                hover
                {...buttonProps}
              />
            </ScrollingForm>
            {!msg && (
              <p className="ml-2">
                {showSeatCount ? (
                  <span>
                    Using {numPaidSeatsInUse} of{' '}
                    {asQuantityWithUnit(
                      'paid seats',
                      'paid seat',
                    )(Math.abs(numPaidSeats))}
                  </span>
                ) : (
                  !hasValidSubscription && <span>Invalid subscription</span>
                )}
                {showSettingsLink && (
                  <span>
                    <Endash />
                    <Link to={getTeamBillingPath(data?.team)}>
                      Edit subscription
                    </Link>
                  </span>
                )}
              </p>
            )}
          </>
        )}
      </Formik>
    </ShrinkContentColumn>
  );
};

export default TeamMembersTable;
